import { useState } from 'react';
import { Plus, ChevronLeft, ChevronRight, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AppLayout } from '@/components/layout/AppLayout';
import { TransactionForm } from '@/components/transactions/TransactionForm';
import { TransactionsList } from '@/components/transactions/TransactionsList';
import { DataImportDialog } from '@/components/import/DataImportDialog';
import BankStatementImport from '@/components/import/BankStatementImport';
import { BudgetAlertBanner } from '@/components/budget/BudgetAlertBanner';
import { BudgetManager } from '@/components/budget/BudgetManager';
import { PremiumGate } from '@/components/subscription/PremiumGate';
import { useTransactions, Transaction, CreateTransactionInput } from '@/hooks/useTransactions';
import { useBudgetAlerts } from '@/hooks/useBudgetAlerts';
import { useProfile } from '@/hooks/useProfile';
import { useToast } from '@/hooks/use-toast';

const monthNames = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

export default function Transactions() {
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [formOpen, setFormOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const { toast } = useToast();
  const { profile } = useProfile();
  const currency = profile?.default_currency || 'CRC';

  const { transactions, isLoading, createTransaction, updateTransaction, deleteTransaction, bulkCreateTransactions, bulkUpdateCategory, bulkDeleteTransactions } = 
    useTransactions(selectedYear, selectedMonth);
  const { warningAlerts, exceededAlerts, hasAlerts } = useBudgetAlerts(selectedMonth, selectedYear);

  const handleImportTransactions = async (data: Record<string, unknown>[]) => {
    await bulkCreateTransactions.mutateAsync(data as unknown as CreateTransactionInput[]);
    toast({
      title: 'Importación exitosa',
      description: `Se importaron ${data.length} transacciones correctamente`,
    });
  };

  const handlePreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const handleCreate = (data: CreateTransactionInput) => {
    createTransaction.mutate(data);
  };

  const handleEdit = (transaction: Transaction) => {
    setEditingTransaction(transaction);
    setFormOpen(true);
  };

  const handleUpdate = (data: CreateTransactionInput) => {
    if (editingTransaction) {
      updateTransaction.mutate({ id: editingTransaction.id, ...data });
      setEditingTransaction(null);
    }
  };

  const handleDelete = (id: string) => {
    deleteTransaction.mutate(id);
  };

  const handleBulkUpdateCategory = (ids: string[], category: string) => {
    bulkUpdateCategory.mutate({ ids, category });
  };

  const handleBulkDelete = (ids: string[]) => {
    bulkDeleteTransactions.mutate(ids);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingTransaction(null);
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">Transacciones</h1>
            <p className="text-muted-foreground">
              Registra y gestiona tus ingresos, gastos, ahorros e inversiones
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Month selector */}
            <div className="flex items-center gap-2 bg-card rounded-lg border p-1">
              <Button variant="ghost" size="icon" onClick={handlePreviousMonth}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="px-4 py-2 font-medium min-w-[160px] text-center">
                {monthNames[selectedMonth - 1]} {selectedYear}
              </span>
              <Button variant="ghost" size="icon" onClick={handleNextMonth}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            <BankStatementImport 
              variant="compact" 
              onImportComplete={(imported) => {
                if (imported.length > 0) {
                  const firstDate = new Date(imported[0].date);
                  setSelectedYear(firstDate.getFullYear());
                  setSelectedMonth(firstDate.getMonth() + 1);
                }
              }}
            />

            <DataImportDialog
              type="transactions"
              onImport={handleImportTransactions}
              trigger={
                <Button variant="outline">
                  <Upload className="h-4 w-4 mr-2" />
                  Importar Excel
                </Button>
              }
            />

            <Button onClick={() => setFormOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Nueva transacción
            </Button>
          </div>
        </div>

        {/* Budget Alerts */}
        {hasAlerts && (
          <BudgetAlertBanner 
            warningAlerts={warningAlerts} 
            exceededAlerts={exceededAlerts} 
            currency={currency} 
          />
        )}

        {/* Budget Manager */}
        <PremiumGate feature="budgets" featureLabel="Topes de gastos por categoría">
          <BudgetManager />
        </PremiumGate>

        {/* Transactions list */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : (
          <TransactionsList
            transactions={transactions}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onBulkUpdateCategory={handleBulkUpdateCategory}
            onBulkDelete={handleBulkDelete}
          />
        )}

        {/* Transaction form modal */}
        <TransactionForm
          key={editingTransaction?.id || 'new'}
          open={formOpen}
          onOpenChange={handleCloseForm}
          onSubmit={editingTransaction ? handleUpdate : handleCreate}
          initialData={editingTransaction || undefined}
          isEditing={!!editingTransaction}
        />
      </div>
    </AppLayout>
  );
}
